import { MutationResult } from '@angular-architects/ngrx-toolkit';
import { Component, computed, inject, Input, OnDestroy, signal, Signal, TemplateRef, ViewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DialogPosition, MatDialogModule } from '@angular/material/dialog';
import { SpinnerComponent } from '@libs/common/components';
import { DialogService } from '@libs/shared/common';
import { FormattedPrice, ProductModel, ShoppingListItemModel, ShoppingListModel } from '@libs/shop/models';
import { PricesFormatter, ShoppingListsState, ViewportService } from '@libs/shared/services';
import { AddToShoppingListComponent, AddToShoppingListFormResult, NewShoppingListId } from '../add-to-shopping-list';

@Component({
  selector: 'ui-add-to-shopping-list-dialog',
  templateUrl: './add-to-shopping-list-dialog.component.html',
  styleUrls: ['./add-to-shopping-list-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogModule,
    AddToShoppingListComponent,
    SpinnerComponent,
  ],
})
export class AddToShoppingListDialogComponent implements OnDestroy {
  @Input() public sku?: string;
  @Input() public label?: string;
  @Input() public amount?: number = 1;

  @ViewChild('dialogContent') public dialogContentRef!: TemplateRef<unknown>;

  public shoppingLists: Signal<ShoppingListModel[]> = this.shoppingListsState.entities;

  public addToShoppingListError: WritableSignal<unknown> = signal(undefined);

  public errorText: Signal<string | undefined> = computed(() => {
    if (this.shoppingListsState.error()) {
      return 'Die Einkaufslisten konnten nicht geladen werden. Bitte versuchen Sie es später noch einmal oder kontaktieren Sie den Support.';
    }

    if (this.addToShoppingListError()) {
      return `${this.label as string} konnte nicht zur Einkaufsliste hinzugefügt werden.`;
    }

    return undefined;
  });

  public loading: Signal<boolean> = this.shoppingListsState.isLoading;
  private readonly pricesFormatter: PricesFormatter = inject(PricesFormatter);

  private readonly isMobile: Signal<boolean> = toSignal(this.viewportService.isMobile(), { initialValue: false });

  public constructor(
    private readonly shoppingListsState: ShoppingListsState,
    private readonly dialogService: DialogService,
    private readonly viewportService: ViewportService,
  ) {
  }

  public ngOnDestroy(): void {
    this.closeDialog();
  }

  public async onSubmit(values: AddToShoppingListFormResult): Promise<void> {
    let listId: string = values.shoppingList;

    if (values.shoppingList === NewShoppingListId) {
      if (!values.newListName) {
        return;
      }

      const newListMutationResult: MutationResult<ShoppingListModel> = await this.shoppingListsState.createNewShoppingList(values.newListName);

      if (newListMutationResult.status === 'error') {
        this.addToShoppingListError.set(newListMutationResult.error);
      }

      if (newListMutationResult.status !== 'success') {
        return;
      }

      listId = newListMutationResult.value.id;
    }

    const addToShoppingListMutationResult: MutationResult<ShoppingListItemModel> = await this.shoppingListsState.addProductToShoppingList({
      listId: listId,
      sku: this.sku as string,
      quantity: values.productQuantity,
    });

    if (addToShoppingListMutationResult.status === 'error') {
      this.addToShoppingListError.set(addToShoppingListMutationResult.error);

      return;
    }

    this.closeDialog();
  }

  public retry(): void {
    this.shoppingListsState.reload();
    this.addToShoppingListError.set(undefined);
  }

  public closeDialog(): void {
    // dialog ref close does not work in all scenarios.
    // there should only be one dialog at once
    this.dialogService.closeAll();
    this.addToShoppingListError.set(undefined);
  }

  public openDialogWithProduct(product: ProductModel, amount: number): void {
    const formatedAmount: FormattedPrice = this.pricesFormatter.formatPrice(product.prices?.endUser ?? 0);
    const price: string = `VP: ${formatedAmount.priceSlicedToHundredth}<sup>${formatedAmount.thousandsPlace}</sup> €`;

    this.sku = product.sku;
    this.label = `${product.details.name} | Art.Nr.: ${product.sku} | ${product.details.packagingUnit} | ${price}`;
    this.amount = amount;
    this.openDialog();
  }

  public openDialog(): void {
    let position: DialogPosition | undefined = undefined;

    if (this.isMobile()) {
      position = {
        top: 'calc(2.5vh + env(safe-area-inset-top))',
      };
    }

    this.dialogService.open(this.dialogContentRef, {
      panelClass: ['flex-dialog-container', 'add-to-shopping-list'],
      position,
    });
  }
}
